// @ts-check
import { themeVariables } from "./themeVariables.js";

export const CONTRACT_DEFAULT_ID = "default";
export const SWATCH_VARS = ["--nav-bg", "--button-bg", "--table-row-selected-bg", "--page"];

/**
 * @param {string | null | undefined} localPref
 */
export function initialThemeState(localPref) {
  return {
    nextTicket: 1,
    appliedTicket: 0,
    localPref: typeof localPref === "string" && localPref !== "" ? localPref : null,
    brandingStatus: /** @type {"pending" | "ok" | "failed"} */ ("pending"),
    /** @type {Record<string, unknown>[]} */
    brands: [],
    /** @type {import("./resolveTheme.js").ThemeDocument[]} */
    documents: [],
    /** @type {{ id: string, label: string, isBrandDefault: boolean, swatches: string[] }[]} */
    summaries: [],
    /** @type {string | null} */
    brandThemeId: null,
    /** @type {{ id: string, ticket: number } | null} */
    pendingServer: null,
    /** @type {{ id: string | null, ticket: number } | null} */
    saveRequest: null,
    prefWrite: 0,
  };
}

/**
 * @param {import("./resolveTheme.js").ThemeDocument} doc
 * @param {import("./resolveTheme.js").ThemeContract} contract
 */
function summarise(doc, contract) {
  const { vars } = themeVariables(doc, contract);
  const id = typeof doc.id === "string" ? doc.id : "";
  return {
    id,
    label: typeof doc.label === "string" && doc.label !== "" ? doc.label : id,
    isBrandDefault: false,
    swatches: SWATCH_VARS.map((name) => vars[name] ?? ""),
  };
}

/**
 * @param {{ id: string, isBrandDefault: boolean }[]} summaries
 * @param {string | null} brandThemeId
 */
function markDefault(summaries, brandThemeId) {
  return summaries.map((item) => ({ ...item, isBrandDefault: item.id === brandThemeId }));
}

/**
 * @param {unknown[] | undefined} themes
 * @param {import("./resolveTheme.js").ThemeContract} contract
 */
export function buildThemeList(themes, contract) {
  /** @type {import("./resolveTheme.js").ThemeDocument[]} */
  const documents = [];
  /** @type {ReturnType<typeof summarise>[]} */
  const summaries = [];
  const seen = new Set();
  for (const theme of themes ?? []) {
    if (!theme || typeof theme !== "object" || Array.isArray(theme)) continue;
    const doc = /** @type {import("./resolveTheme.js").ThemeDocument} */ (theme);
    if (typeof doc.id !== "string" || doc.id === "" || seen.has(doc.id)) continue;
    seen.add(doc.id);
    documents.push(doc);
    summaries.push(summarise(doc, contract));
  }
  return { documents, summaries };
}

/**
 * @param {{ name?: string, themeId?: string | null } | null | undefined} brand
 */
export function brandThemeIdFor(brand) {
  if (!brand) return null;
  if (typeof brand.themeId === "string" && brand.themeId !== "") return brand.themeId;
  if (typeof brand.name === "string" && brand.name !== "") return brand.name;
  return null;
}

/**
 * @param {{ localPref?: string | null, brandThemeId?: string | null, themes?: { id: string }[] }} state
 */
export function pickThemeId(state) {
  const themes = state.themes ?? [];
  const known = (id) => typeof id === "string" && id !== "" && themes.some((theme) => theme.id === id);
  if (known(state.localPref)) return /** @type {string} */ (state.localPref);
  if (known(state.brandThemeId)) return /** @type {string} */ (state.brandThemeId);
  return CONTRACT_DEFAULT_ID;
}

/**
 * @param {ReturnType<typeof initialThemeState>} state
 * @param {string} id
 * @param {number} ticket
 */
function applyServer(state, id, ticket) {
  return {
    ...state,
    localPref: id,
    appliedTicket: ticket,
    pendingServer: null,
    prefWrite: state.prefWrite + 1,
  };
}

/**
 * @param {ReturnType<typeof initialThemeState>} state
 * @param {{ type: string, ticket?: number, id?: string | null, save?: boolean, brands?: unknown[], themes?: unknown[], contract?: import("./resolveTheme.js").ThemeContract, domainBrand?: { name?: string, themeId?: string | null } | null }} action
 */
export function themeReducer(state, action) {
  const ticket = action.ticket ?? 0;
  const fresh = ticket > state.appliedTicket;
  if (action.type === "BRANDING_LOADED" && action.contract) {
    const built = buildThemeList(action.themes, action.contract);
    const brandThemeId = brandThemeIdFor(action.domainBrand);
    let next = {
      ...state,
      brandingStatus: /** @type {"ok"} */ ("ok"),
      brands: /** @type {Record<string, unknown>[]} */ (action.brands ?? []),
      documents: built.documents,
      summaries: markDefault(built.summaries, brandThemeId),
      brandThemeId,
    };
    if (state.pendingServer && state.pendingServer.ticket >= state.appliedTicket) {
      next = applyServer(next, state.pendingServer.id, state.pendingServer.ticket);
    }
    return next;
  }
  if (action.type === "BRANDING_FAILED") {
    return {
      ...state,
      brandingStatus: /** @type {"failed"} */ ("failed"),
      brands: [],
      documents: [],
      summaries: [],
      brandThemeId: null,
    };
  }
  if (!fresh) return state;
  if (action.type === "SERVER_PREFERENCE") {
    if (action.id == null || action.id === "") {
      return { ...state, appliedTicket: ticket, pendingServer: null };
    }
    if (state.brandingStatus === "pending") {
      return { ...state, appliedTicket: ticket, pendingServer: { id: action.id, ticket } };
    }
    return applyServer(state, action.id, ticket);
  }
  if (action.type === "USER_SELECTED") {
    const id = action.id ?? null;
    return {
      ...state,
      localPref: id,
      appliedTicket: ticket,
      pendingServer: null,
      prefWrite: state.prefWrite + 1,
      saveRequest: action.save ? { id, ticket } : null,
    };
  }
  if (action.type === "SIGNED_OUT") {
    return {
      ...state,
      localPref: null,
      appliedTicket: ticket,
      pendingServer: null,
      prefWrite: state.prefWrite + 1,
      saveRequest: null,
    };
  }
  return state;
}

/**
 * @param {ReturnType<typeof initialThemeState>} state
 */
export function currentThemeId(state) {
  return pickThemeId({
    localPref: state.localPref,
    brandThemeId: state.brandThemeId,
    themes: state.summaries,
  });
}

/**
 * @param {ReturnType<typeof initialThemeState>} state
 */
export function documentFor(state) {
  const id = currentThemeId(state);
  if (id === CONTRACT_DEFAULT_ID) return null;
  return state.documents.find((doc) => doc.id === id) ?? null;
}
